// frontend/src/pages/Partner.tsx
import dayjs from 'dayjs'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DefaultLayout from '../components/layouts/DefaultLayout'
import {
  Box,
  Text,
  Stack,
  SimpleGrid,
  HStack,
  VStack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  InputGroup,
  Input,
  InputLeftElement,
  Icon,
  Badge,
  Skeleton,
  useToast,
  Avatar,
  Divider,
  Tooltip,
  Button,
  Container,
  Tag
} from '@chakra-ui/react'
import {
  FiUsers,
  FiUser,
  FiPhone,
  FiSearch,
  FiCalendar,
  FiTrendingUp,
  FiDollarSign,
  FiClock
} from 'react-icons/fi'
import { useRecoilState } from 'recoil'
import { authState } from '../atoms/auth'
import { getPartnerList } from '../services/apiService'
import type { AxiosResponse } from 'axios'

type DirectUser = {
  id: number
  account: string
  username: string
  email: string | null
  phone: string | null
  grade: string | null
  serviceAt: string | null
  referralAt: string | null
  subscriptionAt: string | null
  createdAt: string
  updatedAt: string
}

type PartnerSalesItem = {
  userId: number
  account: string
  username: string
  salesUSDT: string
  salesCount: number
  lastSaleAt: string | null
}

type RewardItem = {
  id: number
  usdtAmount: string
  byUserId: number
  byHistoryId: number
  note1: string | null
  note2: string | null
  note3: string | null
  createdAt: string
  payoutScheduledDate: string | null
  payoutStatus: 'PENDING' | 'PAID' | string | null
}

type MyRewards = {
  totalRewardUSDT: string
  pendingRewardUSDT: string
  items: RewardItem[]
}

type PartnerData = {
  directList: DirectUser[]
  directCount: number
  totalDescendantCount: number
  activeDirectList: DirectUser[]
  activeDirectCount: number
  partnerSales: PartnerSalesItem[]
  myRewards: MyRewards
}

type CommonResponse = {
  statusCode: number
  message: string
  data: PartnerData
}

const isValidFuture = (iso?: string | null) => {
  if (!iso) return false
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return false
  return t > Date.now()
}

const formatDate = (iso?: string | null) => {
  if (!iso) return '-'
  const d = dayjs(iso)
  if (!d.isValid()) return '-'
  return d.format('YYYY.MM.DD HH:mm:ss')
}

const formatUSDT = (v?: string | number | null) => {
  if (v === null || v === undefined) return '0'
  const n = typeof v === 'string' ? Number(v) : v
  if (!Number.isFinite(n)) return String(v)
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 8
  })
}

const Partner = () => {
  const { t } = useTranslation()
  const toast = useToast()
  const [auth] = useRecoilState(authState)

  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<PartnerData | null>(null)
  const [query, setQuery] = useState('')

  const load = async () => {
    try {
      setLoading(true)
      const res = (await getPartnerList()) as AxiosResponse<CommonResponse>
      if (res?.data?.statusCode === 200) {
        setData(res.data.data)
      } else {
        throw new Error(
          res?.data?.message || t('파트너 정보를 불러오지 못했습니다.')
        )
      }
    } catch (err: any) {
      toast({
        status: 'error',
        title: t('불러오기 실패'),
        description: err?.message || t('파트너 정보를 불러오지 못했습니다.'),
        duration: 4000,
        isClosable: true
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const filtered = useMemo(() => {
    const list = data?.directList || []
    if (!query.trim()) return list
    const q = query.toLowerCase()
    return list.filter((u) =>
      [u.account, u.username, u.phone ?? '', u.email ?? '']
        .join(' ')
        .toLowerCase()
        .includes(q)
    )
  }, [data, query])

  // 지급상태 표시(사람이 읽는 언어로)
  const payoutLabel = (status?: string | null) => {
    if (status === 'PENDING') return t('대기')
    if (status === 'PAID') return t('지급완료')
    return status || 'UNKNOWN'
  }

  return (
    <DefaultLayout>
      <Container maxW="7xl" py={8}>
        <Stack spacing={6}>
          {/* 헤더 */}
          <VStack align="stretch" spacing={2}>
            <HStack justify="space-between">
              <HStack spacing={3}>
                <Icon as={FiUsers} boxSize={6} />
                <Text fontSize="2xl" fontWeight="bold">
                  {t('파트너 현황')}
                </Text>
              </HStack>
              <Button
                variant="ghost"
                size="sm"
                onClick={load}
                isLoading={loading}
              >
                {t('새로고침')}
              </Button>
            </HStack>
            <Text color="gray.500">
              {t(
                '직접 추천, 활성 파트너, 산하 전체 규모와 파트너 매출/나의 보상을 한 눈에 확인하세요.'
              )}
            </Text>
          </VStack>

          {/* 통계 카드 */}
          <SimpleGrid columns={{ base: 1, md: 3, xl: 5 }} gap={4}>
            <Stat
              p={5}
              rounded="2xl"
              borderWidth="1px"
              bg="whiteAlpha.700"
              backdropFilter="auto"
              backdropBlur="4px"
              shadow="sm"
            >
              <StatLabel fontWeight="semibold">
                {t('직접 추천 파트너')}
              </StatLabel>
              <Skeleton isLoaded={!loading}>
                <StatNumber fontSize="3xl">{data?.directCount ?? 0}</StatNumber>
              </Skeleton>
              <StatHelpText>{t('내가 직접 초대한 파트너')}</StatHelpText>
            </Stat>

            <Stat
              p={5}
              rounded="2xl"
              borderWidth="1px"
              bg="whiteAlpha.700"
              backdropFilter="auto"
              backdropBlur="4px"
              shadow="sm"
            >
              <StatLabel fontWeight="semibold">
                {t('활성화 추천 파트너')}
              </StatLabel>
              <Skeleton isLoaded={!loading}>
                <StatNumber fontSize="3xl">
                  {data?.activeDirectCount ?? 0}
                </StatNumber>
              </Skeleton>
              <StatHelpText>{t('봇 이용일자가 유효한 파트너')}</StatHelpText>
            </Stat>

            <Stat
              p={5}
              rounded="2xl"
              borderWidth="1px"
              bg="whiteAlpha.700"
              backdropFilter="auto"
              backdropBlur="4px"
              shadow="sm"
            >
              <StatLabel fontWeight="semibold">
                {t('산하 전체 파트너 수')}
              </StatLabel>
              <Skeleton isLoaded={!loading}>
                <StatNumber fontSize="3xl">
                  {data?.totalDescendantCount ?? 0}
                </StatNumber>
              </Skeleton>
              <StatHelpText>{t('내 트리 전체(모든 레벨)')}</StatHelpText>
            </Stat>

            <Stat
              p={5}
              rounded="2xl"
              borderWidth="1px"
              bg="whiteAlpha.700"
              backdropFilter="auto"
              backdropBlur="4px"
              shadow="sm"
            >
              <StatLabel fontWeight="semibold">{t('나의 보상 총액')}</StatLabel>
              <Skeleton isLoaded={!loading}>
                <StatNumber fontSize="3xl">
                  {formatUSDT(data?.myRewards?.totalRewardUSDT)} USDT
                </StatNumber>
              </Skeleton>
              <StatHelpText>{t('영업활동에 대한 누적 보상')}</StatHelpText>
            </Stat>

            <Stat
              p={5}
              rounded="2xl"
              borderWidth="1px"
              bg="whiteAlpha.700"
              backdropFilter="auto"
              backdropBlur="4px"
              shadow="sm"
            >
              <StatLabel fontWeight="semibold">{t('대기 중 보상')}</StatLabel>
              <Skeleton isLoaded={!loading}>
                <StatNumber fontSize="3xl">
                  {formatUSDT(data?.myRewards?.pendingRewardUSDT)} USDT
                </StatNumber>
              </Skeleton>
              <StatHelpText>{t('영업활동에 대한 대기중인 보상')}</StatHelpText>
            </Stat>
          </SimpleGrid>

          {/* 검색 */}
          <Box>
            <InputGroup maxW="400px">
              <InputLeftElement pointerEvents="none">
                <Icon as={FiSearch} />
              </InputLeftElement>
              <Input
                placeholder={t('계정/이름/전화/이메일 검색')}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                rounded="xl"
              />
            </InputGroup>
          </Box>

          {/* 직접 추천 리스트 */}
          <VStack align="stretch" spacing={3}>
            <HStack>
              <Text fontSize="xl" fontWeight="bold">
                {t('직접 추천 파트너')}
              </Text>
              <Badge colorScheme="blue" rounded="lg">
                {filtered.length} / {data?.directCount ?? 0}
              </Badge>
              <Tag size="sm" colorScheme="green">
                {t('활성 {{count}}', { count: data?.activeDirectCount ?? 0 })}
              </Tag>
            </HStack>
            <Divider />

            {/* 로딩 스켈레톤 */}
            {loading && (
              <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} gap={4}>
                {[...Array(6)].map((_, i) => (
                  <Box
                    key={i}
                    p={5}
                    borderWidth="1px"
                    rounded="2xl"
                    shadow="sm"
                  >
                    <Skeleton height="20px" mb={3} />
                    <Skeleton height="16px" mb={2} />
                    <Skeleton height="16px" mb={2} />
                    <Skeleton height="16px" />
                  </Box>
                ))}
              </SimpleGrid>
            )}

            {/* 비어있을 때 */}
            {!loading && filtered.length === 0 && (
              <Box
                p={8}
                borderWidth="1px"
                rounded="2xl"
                textAlign="center"
                color="gray.500"
              >
                {t(
                  '아직 직접 추천한 사용자가 없거나, 검색 조건에 맞는 결과가 없어요.'
                )}
              </Box>
            )}

            {/* 카드 그리드 */}
            {!loading && filtered.length > 0 && (
              <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} gap={4}>
                {filtered.map((u) => (
                  <Box
                    key={u.id}
                    p={5}
                    borderWidth="1px"
                    rounded="2xl"
                    shadow="sm"
                    _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
                    transition="all 0.2s ease"
                  >
                    <HStack align="flex-start" spacing={4}>
                      <Avatar name={u.account} />
                      <VStack align="stretch" spacing={1} flex={1}>
                        <HStack justify="space-between">
                          <HStack>
                            <Text fontWeight="bold">{u.account || '-'}</Text>
                          </HStack>
                          {/* 등급/상태 뱃지 (subscription/referral/service) */}
                          {(() => {
                            const isSecret = isValidFuture(u.subscriptionAt)
                            const isVip =
                              !isSecret && isValidFuture(u.referralAt)
                            const isNomal =
                              !isSecret && !isVip && isValidFuture(u.serviceAt)
                            const label = isSecret
                              ? t('SECRET')
                              : isVip
                              ? t('VIP')
                              : isNomal
                              ? t('NOMAL')
                              : t('NORMAL')
                            const colorScheme = isSecret
                              ? 'red'
                              : isVip
                              ? 'yellow'
                              : isNomal
                              ? 'blue'
                              : 'gray'
                            return (
                              <Badge
                                colorScheme={colorScheme}
                                variant="subtle"
                                rounded="md"
                              >
                                {label}
                              </Badge>
                            )
                          })()}
                        </HStack>

                        <HStack color="gray.600">
                          <Icon as={FiUser} />
                          <Text fontFamily="mono" fontSize="sm">
                            {u.username}
                          </Text>
                        </HStack>
                        <HStack color="gray.600">
                          <Icon as={FiPhone} />
                          <Text fontSize="sm">{u.phone || '-'}</Text>
                        </HStack>

                        <Divider my={2} />

                        <VStack
                          align="stretch"
                          spacing={1}
                          color="gray.600"
                          fontSize="sm"
                        >
                          <HStack>
                            <Icon as={FiCalendar} />
                            <Tooltip label={t('서비스 유효일')}>
                              <Text>
                                {t('서비스')}: {formatDate(u.serviceAt)}
                              </Text>
                            </Tooltip>
                          </HStack>
                          <HStack>
                            <Icon as={FiCalendar} />
                            <Tooltip label={t('레퍼럴 수익 유효일')}>
                              <Text>
                                {t('레퍼럴')}: {formatDate(u.referralAt)}
                              </Text>
                            </Tooltip>
                          </HStack>
                          <HStack>
                            <Icon as={FiCalendar} />
                            <Tooltip label={t('구독료 수익 유효일')}>
                              <Text>
                                {t('구독료')}: {formatDate(u.subscriptionAt)}
                              </Text>
                            </Tooltip>
                          </HStack>
                          <HStack>
                            <Icon as={FiCalendar} />
                            <Tooltip label={t('가입일')}>
                              <Text>
                                {t('가입')}: {formatDate(u.createdAt)}
                              </Text>
                            </Tooltip>
                          </HStack>
                        </VStack>
                      </VStack>
                    </HStack>
                  </Box>
                ))}
              </SimpleGrid>
            )}
          </VStack>

          {/* 파트너 매출 섹션 */}
          <VStack align="stretch" spacing={3}>
            <HStack>
              <Icon as={FiTrendingUp} />
              <Text fontSize="xl" fontWeight="bold">
                {t('파트너 매출')}
              </Text>
              <Tag size="sm" colorScheme="purple">
                {t('합계 {{amount}} USDT', {
                  amount: formatUSDT(
                    (data?.partnerSales || []).reduce(
                      (s, x) => s + Number(x.salesUSDT || 0),
                      0
                    )
                  )
                })}
              </Tag>
            </HStack>
            <Divider />
            {loading && (
              <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} gap={4}>
                {[...Array(3)].map((_, i) => (
                  <Box
                    key={i}
                    p={5}
                    borderWidth="1px"
                    rounded="2xl"
                    shadow="sm"
                  >
                    <Skeleton height="20px" mb={3} />
                    <Skeleton height="16px" mb={2} />
                    <Skeleton height="16px" />
                  </Box>
                ))}
              </SimpleGrid>
            )}
            {!loading && (data?.partnerSales?.length ?? 0) === 0 && (
              <Box
                p={6}
                borderWidth="1px"
                rounded="2xl"
                textAlign="center"
                color="gray.500"
              >
                {t('파트너 매출 데이터가 없습니다.')}
              </Box>
            )}
            {!loading && (data?.partnerSales?.length ?? 0) > 0 && (
              <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} gap={4}>
                {data!.partnerSales.map((s) => (
                  <Box
                    key={s.userId}
                    p={5}
                    borderWidth="1px"
                    rounded="2xl"
                    shadow="sm"
                  >
                    <HStack justify="space-between" mb={2}>
                      <HStack>
                        <Avatar name={s.account} size="sm" />
                        <VStack spacing={0} align="start">
                          <Text fontWeight="bold">{s.account}</Text>
                          <Text fontSize="sm" color="gray.600">
                            {s.username}
                          </Text>
                        </VStack>
                      </HStack>
                      <Tag colorScheme="blue">
                        {t('{{count}} 건', { count: s.salesCount })}
                      </Tag>
                    </HStack>
                    <HStack color="gray.700" mb={1}>
                      <Icon as={FiDollarSign} />
                      <Text fontWeight="semibold">
                        {formatUSDT(s.salesUSDT)} USDT
                      </Text>
                    </HStack>
                    <HStack color="gray.600" fontSize="sm">
                      <Icon as={FiClock} />
                      <Text>
                        {t('마지막 매출')}: {formatDate(s.lastSaleAt)}
                      </Text>
                    </HStack>
                  </Box>
                ))}
              </SimpleGrid>
            )}
          </VStack>

          {/* 나의 보상 섹션 */}
          <VStack align="stretch" spacing={3}>
            <HStack>
              <Icon as={FiDollarSign} />
              <Text fontSize="xl" fontWeight="bold">
                {t('나의 보상')}
              </Text>
              <Tag size="sm" colorScheme="teal">
                {t('총 {{amount}} USDT', {
                  amount: formatUSDT(data?.myRewards?.totalRewardUSDT)
                })}
              </Tag>
              <Tag size="sm" colorScheme="orange">
                {t('대기 {{amount}} USDT', {
                  amount: formatUSDT(data?.myRewards?.pendingRewardUSDT)
                })}
              </Tag>
            </HStack>
            <Divider />
            {loading && (
              <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} gap={4}>
                {[...Array(3)].map((_, i) => (
                  <Box
                    key={i}
                    p={5}
                    borderWidth="1px"
                    rounded="2xl"
                    shadow="sm"
                  >
                    <Skeleton height="20px" mb={3} />
                    <Skeleton height="16px" mb={2} />
                    <Skeleton height="16px" />
                  </Box>
                ))}
              </SimpleGrid>
            )}
            {!loading && (data?.myRewards?.items?.length ?? 0) === 0 && (
              <Box
                p={6}
                borderWidth="1px"
                rounded="2xl"
                textAlign="center"
                color="gray.500"
              >
                {t('보상 이력이 없습니다.')}
              </Box>
            )}
            {!loading && (data?.myRewards?.items?.length ?? 0) > 0 && (
              <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} gap={4}>
                {data!.myRewards.items.map((r) => (
                  <Box
                    key={r.id}
                    p={5}
                    borderWidth="1px"
                    rounded="2xl"
                    shadow="sm"
                  >
                    <HStack justify="space-between" mb={2}>
                      <HStack>
                        <Icon as={FiDollarSign} />
                        <Text fontWeight="bold">
                          {formatUSDT(r.usdtAmount)} USDT
                        </Text>
                      </HStack>
                      <Tag
                        colorScheme={
                          r.payoutStatus === 'PENDING' ? 'orange' : 'green'
                        }
                      >
                        {payoutLabel(r.payoutStatus)}
                      </Tag>
                    </HStack>
                    {r.note1 && (
                      <Text fontSize="sm" color="gray.700" mb={1}>
                        {r.note1}
                      </Text>
                    )}
                    {r.note2 && (
                      <Text fontSize="xs" color="gray.500" mb={1}>
                        {r.note2}
                      </Text>
                    )}
                    <VStack
                      align="start"
                      spacing={1}
                      color="gray.600"
                      fontSize="sm"
                      mt={2}
                    >
                      <HStack>
                        <Icon as={FiCalendar} />
                        <Text>
                          {t('발생일')}: {formatDate(r.createdAt)}
                        </Text>
                      </HStack>
                      <HStack>
                        <Icon as={FiClock} />
                        <Text>
                          {t('지급 예정')}: {r.payoutScheduledDate ?? '-'}
                        </Text>
                      </HStack>
                    </VStack>
                  </Box>
                ))}
              </SimpleGrid>
            )}
          </VStack>
        </Stack>
      </Container>
    </DefaultLayout>
  )
}

export default Partner
